import { mergeProps } from "@base-ui/react/merge-props"
import { useRender } from "@base-ui/react/use-render"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/frontend/lib/utils"

const badgeVariants = cva(
  "group/badge inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border-[1.2px] border-transparent font-medium whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&>svg]:pointer-events-none [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        // Status roles
        neutral: "bg-neutral text-neutral-fg border-neutral-border",
        info: "bg-info text-info-fg border-info-border",
        success: "bg-success text-success-fg border-success-border",
        pending: "bg-pending text-pending-fg border-pending-border",
        warning: "bg-warning text-warning-fg border-warning-border",
        destructive: "bg-destructive text-destructive-fg border-destructive-border",

        // Non-status variants.
        default: "bg-primary text-primary-foreground [a]:hover:bg-primary/80",
        secondary:
          "bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80",
        outline:
          "border-neutral-300 text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground",
        ghost: "hover:bg-muted hover:text-muted-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-6 px-2.5 py-1 text-sm [&>svg:not([class*='size-'])]:size-3.5",
        lg: "h-7 px-3 py-1.5 text-base [&>svg:not([class*='size-'])]:size-4",
        sm: "h-5 px-2 py-0.5 text-xs [&>svg:not([class*='size-'])]:size-3",
        xs: "h-4.5 px-1.5 py-0 text-2xs [&>svg:not([class*='size-'])]:size-2.5"
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export type BadgeVariants = VariantProps<typeof badgeVariants>

function Badge({
  className,
  variant = "default",
  size = "default",
  render,
  ...props
}: useRender.ComponentProps<"span"> & BadgeVariants) {
  return useRender({
    defaultTagName: "span",
    props: mergeProps<"span">(
      {
        className: cn(badgeVariants({ variant, size }), className),
      },
      props
    ),
    render,
    state: {
      slot: "badge",
      variant,
    },
  })
}

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

export { Badge, badgeVariants }
